import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Alert,
  Button,
  InputNumber,
  Popconfirm,
  Select,
  Switch,
  Table,
  Tag,
  Tooltip,
} from 'antd';
import { ReloadOutlined, StarFilled } from '@ant-design/icons';

import {
  DevRecommendationAdmin,
  deleteRecommendation,
  fetchRecTargets,
  fetchRecommendations,
  generateRecommendation,
  selectors,
  setRecommendationEnabled,
} from 'state';

import { Field, Section } from './inspectorBits';

type Props = { planCode: string };

const RecommendationsPanel: React.FC<Props> = ({ planCode }) => {
  const dispatch = useDispatch();
  const rows = useSelector(selectors.devAdminRecommendations);
  const rowsCode = useSelector(selectors.devAdminRecommendationsCode);
  const targets = useSelector(selectors.devAdminRecTargets);
  const targetsFetched = useSelector(selectors.devAdminRecTargetsFetched);
  const lastGranted = useSelector(selectors.devAdminLastRecGranted);
  const saving = useSelector(selectors.devAdminSaving);
  const error = useSelector(selectors.devAdminError);
  const [showDemo, setShowDemo] = useState(false);

  const [userId, setUserId] = useState<number | undefined>();
  const [sessionId, setSessionId] = useState<number | undefined>();
  const [k, setK] = useState<number>(6);

  useEffect(() => {
    if (showDemo && !targetsFetched) dispatch(fetchRecTargets({}));
  }, [dispatch, targetsFetched, showDemo]);

  useEffect(() => {
    dispatch(fetchRecommendations({ plan: planCode }));
  }, [dispatch, planCode]);

  const sessions = useMemo(
    () => targets.find(t => t.user_id === userId)?.sessions ?? [],
    [targets, userId],
  );

  // Picking a different trainee invalidates the session choice.
  useEffect(() => {
    setSessionId(prev =>
      prev && sessions.some(s => s.id === prev) ? prev : undefined,
    );
  }, [sessions]);

  // Only show rows for the plan we actually fetched (guards a mid-flight plan switch).
  const planRows = rowsCode === planCode ? rows : [];
  const canGenerate = !!userId && !!sessionId && !saving;
  const selectedUsername = targets.find(t => t.user_id === userId)?.username;

  const demoColumns: any[] = [
    { title: 'Trainee', dataIndex: 'username', key: 'username' },
    { title: 'Session', dataIndex: 'session_label', key: 'session' },
    {
      title: 'Nodes',
      key: 'nodes',
      width: 80,
      render: (row: DevRecommendationAdmin) => (
        <Tooltip title={(row.node_codes || []).join(', ')}>
          <Tag>{row.node_count}</Tag>
        </Tooltip>
      ),
    },
    {
      title: 'Live',
      key: 'enabled',
      width: 70,
      align: 'center',
      render: (row: DevRecommendationAdmin) => (
        <Popconfirm
          title={
            row.enabled
              ? `Turn off — this resets ${row.username}'s progress on this tree to zero.`
              : `Turn on — this resets ${row.username}'s progress on this tree and unlocks the path to the recommended nodes.`
          }
          okText="Do it"
          onConfirm={() =>
            dispatch(
              setRecommendationEnabled({ id: row.id, enabled: !row.enabled }),
            )
          }
        >
          <Switch size="small" checked={row.enabled} disabled={!!saving} />
        </Popconfirm>
      ),
    },
    {
      title: '',
      key: 'del',
      width: 70,
      align: 'center',
      render: (row: DevRecommendationAdmin) => (
        <Popconfirm
          title={`Delete — this also resets ${row.username}'s progress on this tree to zero.`}
          okText="Delete"
          okButtonProps={{ danger: true }}
          onConfirm={() => dispatch(deleteRecommendation({ id: row.id }))}
        >
          <Button type="link" danger size="small" disabled={!!saving}>
            Delete
          </Button>
        </Popconfirm>
      ),
    },
  ];

  const advisoryColumns: any[] = [
    { title: 'Trainee', dataIndex: 'username', key: 'username' },
    { title: 'Source session', dataIndex: 'session_label', key: 'session' },
    {
      title: 'Suggested practice',
      key: 'items',
      render: (row: DevRecommendationAdmin) => (
        <ol style={{ paddingLeft: 20, margin: 0 }}>
          {[...(row.items || [])]
            .sort((a, b) => a.rank - b.rank)
            .map(item => (
              <li
                key={`${item.rank}:${item.node_code}:${item.blueprint_slug}`}
                value={item.rank}
              >
                <b>{item.node_code}</b> · {item.blueprint_slug}
                <p style={{ margin: '4px 0 8px' }}>{item.rationale}</p>
                {item.available === false && (
                  <Tag color="orange">
                    {item.unavailable_reason || 'Scenario unavailable'}
                  </Tag>
                )}
              </li>
            ))}
          {!row.items?.length && <span>No verified scenarios.</span>}
        </ol>
      ),
    },
    {
      title: 'Review',
      key: 'status',
      render: (row: DevRecommendationAdmin) => (
        <>
          <Tag
            color={
              row.available === false
                ? 'orange'
                : row.enabled
                  ? 'green'
                  : 'default'
            }
          >
            {row.available === false
              ? 'Needs refresh'
              : row.enabled
                ? 'Visible to trainee'
                : 'Awaiting review'}
          </Tag>
          {row.unavailable_reason && <p>{row.unavailable_reason}</p>}
        </>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (row: DevRecommendationAdmin) => (
        <>
          <Button
            size="small"
            disabled={
              !!saving ||
              (!row.enabled &&
                (!row.content_revision ||
                  row.available === false ||
                  !row.items?.length ||
                  row.items.some(item => item.available === false)))
            }
            onClick={() =>
              dispatch(
                setRecommendationEnabled({
                  id: row.id,
                  enabled: !row.enabled,
                  expected_revision: row.content_revision,
                }),
              )
            }
          >
            {row.enabled ? 'Hide advice' : 'Approve and show'}
          </Button>
          <Popconfirm
            title={`Delete this advice for ${row.username}? Earned progress and coins are preserved.`}
            okText="Delete advice"
            okButtonProps={{ danger: true }}
            onConfirm={() => dispatch(deleteRecommendation({ id: row.id }))}
          >
            <Button type="link" danger size="small" disabled={!!saving}>
              Delete advice
            </Button>
          </Popconfirm>
        </>
      ),
    },
  ];

  return (
    <>
      <Section title="Assessment recommendations" hint="administrator review">
        <p>
          Review the suggested scenarios and reasons before showing advice to
          the trainee. Showing, hiding or deleting advice preserves earned
          progress and coins.
        </p>
        {error && (
          <Alert
            type="error"
            showIcon
            title={error}
            description="Refresh recommendations to review the current content before trying again."
          />
        )}
        <Button
          size="small"
          icon={<ReloadOutlined />}
          disabled={!!saving}
          onClick={() => dispatch(fetchRecommendations({ plan: planCode }))}
        >
          Refresh recommendations
        </Button>
        <Table
          style={{ marginTop: 14 }}
          size="small"
          rowKey="id"
          pagination={false}
          columns={advisoryColumns}
          dataSource={planRows.filter(row => row.mode === 'advisory')}
          locale={{
            emptyText: 'No assessment recommendations on this tree yet.',
          }}
        />
      </Section>
      <Button type="link" onClick={() => setShowDemo(value => !value)}>
        {showDemo
          ? 'Hide demo progression tools'
          : 'Show demo progression tools'}
      </Button>
      {showDemo && (
        <Section
          title="Demo progression boosts"
          hint="demo feature"
          info={
            <>
              Rolls a random set of nodes for one trainee on this tree and
              unlocks the whole prerequisite path leading to them, so those
              nodes become the frontier they have to solve. The assessment is{' '}
              <b>not real</b> — the pick is uniform random.
              <br />
              <br />
              Generating or enabling{' '}
              <b>
                resets that trainee&apos;s progress on this tree to zero
              </b>{' '}
              and rewrites it; disabling or deleting resets it to zero. Scoped
              to one (trainee, tree) — no other trainee or tree is affected, and
              no shared plan content or asset is touched.
            </>
          }
        >
          <Field
            label="Trainee"
            hint="only trainees granted Development Plan access"
          >
            <Select
              style={{ width: '100%' }}
              placeholder={targetsFetched ? 'Pick a trainee' : 'Loading…'}
              value={userId}
              onChange={setUserId}
              showSearch
              optionFilterProp="label"
              options={targets.map(t => ({
                value: t.user_id,
                label: t.username,
              }))}
            />
          </Field>

          <Field label="Session" hint="provenance label — v1 does not read it">
            <Select
              style={{ width: '100%' }}
              placeholder={userId ? 'Pick a session' : 'Pick a trainee first'}
              value={sessionId}
              onChange={setSessionId}
              disabled={!userId}
              options={sessions.map(s => ({ value: s.id, label: s.label }))}
            />
          </Field>

          <Field label="How many nodes" hint="k">
            <InputNumber
              min={1}
              max={20}
              value={k}
              onChange={v => setK(Number(v) || 6)}
            />
          </Field>

          <Popconfirm
            title={
              selectedUsername
                ? `This resets ${selectedUsername}'s progress on this tree and rolls a new set.`
                : 'This resets the trainee’s progress on this tree and rolls a new set.'
            }
            okText="Roll"
            disabled={!canGenerate}
            onConfirm={() =>
              dispatch(
                generateRecommendation({
                  plan_code: planCode,
                  user_id: userId as number,
                  session_id: sessionId as number,
                  k,
                }),
              )
            }
          >
            <Button
              type="primary"
              icon={<ReloadOutlined />}
              disabled={!canGenerate}
              loading={!!saving}
              style={{ marginTop: 4 }}
            >
              Generate / Re-roll
            </Button>
          </Popconfirm>

          {lastGranted !== undefined && (
            <Alert
              style={{ marginTop: 12 }}
              type={lastGranted > 0 ? 'success' : 'warning'}
              showIcon
              icon={<StarFilled />}
              title={
                lastGranted > 0
                  ? `Last roll unlocked ${lastGranted} node${
                      lastGranted === 1 ? '' : 's'
                    } on the path.`
                  : 'Last roll unlocked nothing — every node picked was a root. Re-roll.'
              }
              description={
                lastGranted > 0
                  ? undefined
                  : 'Re-rolling is safe to repeat: each roll resets before it writes, so nothing accumulates.'
              }
            />
          )}

          <Table
            style={{ marginTop: 14 }}
            size="small"
            rowKey="id"
            pagination={false}
            columns={demoColumns}
            dataSource={planRows.filter(row => row.mode === 'boost')}
            locale={{ emptyText: 'No recommendations on this tree yet.' }}
          />
        </Section>
      )}
    </>
  );
};

export default RecommendationsPanel;
